import { Chip, Tooltip } from '@mui/material';
import { Users } from 'lucide-react';
import { useIntl } from 'react-intl';

type Props = {
    /** Viewer sessions with the patient open (0 = closed, chip hidden). */
    activeEditorCount?: number;
    /** Of those, sessions that reported unsaved changes. */
    activeEditingCount?: number;
};

/**
 * Advisory "who else is here" marker for a worklist row: nothing while
 * the patient is closed, a quiet "Open" chip while any viewer session
 * has it open, and an orange "Editing" chip when at least one of those
 * sessions reported unsaved changes on its last presence heartbeat —
 * the case where opening and saving over it may hit a conflict.
 * Freshness follows the list fetch; presence itself is heartbeat-fresh
 * (see useEditorPresence).
 */
export default function PresenceChip({
    activeEditorCount = 0,
    activeEditingCount = 0,
}: Props) {
    const intl = useIntl();
    if (activeEditorCount <= 0) {
        return null;
    }
    const editing = activeEditingCount > 0;
    return (
        <Tooltip
            title={intl.formatMessage(
                {
                    id: editing
                        ? 'patientList.presence.editingTooltip'
                        : 'patientList.presence.openTooltip',
                },
                { count: editing ? activeEditingCount : activeEditorCount }
            )}
            placement="left"
        >
            <Chip
                data-testid="patient-presence-chip"
                size="small"
                variant="outlined"
                color={editing ? 'warning' : 'default'}
                icon={<Users size={11} />}
                label={intl.formatMessage({
                    id: editing
                        ? 'patientList.presence.editing'
                        : 'patientList.presence.open',
                })}
                sx={{
                    height: 18,
                    fontSize: 10,
                    alignSelf: 'center',
                    '& .MuiChip-icon': { ml: 0.5 },
                }}
            />
        </Tooltip>
    );
}
